import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown, Icon } from 'Components';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import CourseDetailModal from 'LearningManager/components/CourseDetailModal';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import { Menu, Spin, Input as AntInput } from 'antd';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { useQuery } from 'LearningManager/pages/CreateAssignment';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { LearningPath } from 'Services/Api/LearningManager/Types';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const SelectCourses: React.FC = observer(() => {
	const { learningManagerStore } = useStores();
	const [showDetails, setShowDetails] = useState(false);
	const [detailActivityId, setDetailActivityId] = useState<
		number | undefined
	>(undefined);
	const [detailLearningPath, setDetailLearningPath] = useState<
		LearningPath | undefined
	>(undefined);
	// Single search box filters both sections by course name.
	const [search, setSearch] = useState('');
	const query = useQuery();
	const isEditMode = Boolean(query.get('assignmentId'));
	const tenantId = useAssignmentTenantId();

	useEffect(() => {
		if (
			!learningManagerStore.learningPaths.length ||
			learningManagerStore.learningPathsTenantId !== tenantId
		) {
			learningManagerStore
				.listLearningPaths({
					offset: 0,
					...(tenantId ? { tenant_id: tenantId } : {})
				})
				.catch(() => undefined);
		}
		if (
			!learningManagerStore.activities.length ||
			learningManagerStore.activitiesTenantId !== tenantId
		) {
			learningManagerStore
				.listActivities({
					offset: 0,
					...(tenantId ? { tenant_id: tenantId } : {})
				})
				.catch(() => undefined);
		}
		// Course types are a shared taxonomy, not tenant data.
		if (!learningManagerStore.activityTypes.length) {
			learningManagerStore.fetchActivityTypes().catch(() => undefined);
		}
	}, [learningManagerStore, tenantId]);

	const openActivityDetails = (activityId: number) => {
		setDetailLearningPath(undefined);
		setDetailActivityId(activityId);
		setShowDetails(true);
	};

	const openLearningPathDetails = (learningPath: LearningPath) => {
		setDetailActivityId(undefined);
		setDetailLearningPath(learningPath);
		setShowDetails(true);
	};

	return (
		<div id="lms-ca-select-courses" className={styles.root}>
			<Heading
				title={isEditMode ? 'Edit Courses' : 'Select Courses'}
				subHeading="Select courses or learning paths to assign"
			/>
			<Filters search={search} onSearchChange={setSearch} />
			<div className={styles.mainContainer}>
				<LearningPaths
					openDetails={openLearningPathDetails}
					search={search}
				/>
				<IndividualCourses
					openDetails={openActivityDetails}
					search={search}
				/>
			</div>
			<CourseDetailModal
				visible={showDetails}
				onClose={() => setShowDetails(false)}
				activityId={detailActivityId}
				learningPath={detailLearningPath}
			/>
		</div>
	);
});

const Filters = ({
	search,
	onSearchChange
}: {
	search: string;
	onSearchChange: (value: string) => void;
}) => {
	const menu = (
		<Menu style={{ borderRadius: '6px' }}>
			<Menu.Item>English</Menu.Item>
			<Menu.Item disabled>Spanish</Menu.Item>
			<Menu.Item disabled>French</Menu.Item>
			<Menu.Item disabled>German</Menu.Item>
		</Menu>
	);

	return (
		<div id="lms-ca-select-courses-filters" className={styles.filter}>
			<AntInput
				id="lms-ca-select-courses-search"
				className={styles.input}
				placeholder="Search"
				value={search}
				onChange={(e) => onSearchChange(e.target.value)}
				suffix={<Icon type="search" role="img" />}
				allowClear
			/>
			<div id="lms-ca-select-courses-language" className={styles.select}>
				<Dropdown overlay={menu} className={styles.dropdown}>
					English
				</Dropdown>
			</div>
		</div>
	);
};

const LearningPaths = observer(
	({
		openDetails,
		search
	}: {
		openDetails: (learningPath: LearningPath) => void;
		search: string;
	}) => {
		const { learningManagerStore } = useStores();
		const { learningPaths, status } = learningManagerStore;

		const {
			draft: {
				customSetup: { selectedLearningPathCourseIds }
			},
			toggleCustomCourseSelection
		} = useStepper();

		const query = search.trim().toLowerCase();
		const cards = learningPaths
			.filter((lp) => !query || lp.name.toLowerCase().includes(query))
			.map((lp, idx) => ({
				id: lp.id,
				title: lp.name,
				courseCount: `${lp.activities.length} course${
					lp.activities.length !== 1 ? 's' : ''
				}`,
				image: PLACEHOLDER_IMAGES[idx % PLACEHOLDER_IMAGES.length]
			}));

		const isLoading = status.listLearningPaths === Status.Pending;

		return (
			<div
				id="lms-ca-select-courses-learning-paths"
				className={styles.learningWrapper}
			>
				<Heading
					icon={<Icon type="pathIcon" role="img" />}
					title="Learning Paths"
					subHeading="A structured series of courses for effective cybersecurity learning"
				/>
				{isLoading ? (
					<div className={styles.loading}>
						<Spin />
					</div>
				) : cards.length === 0 ? (
					<div className={styles.noResults}>
						No learning paths found
					</div>
				) : (
					<Carousel
						data={cards}
						itemsPerPage={4}
						renderItem={(card) => (
							<CourseCard
								key={card.id}
								index={card.id}
								htmlId={`lms-ca-lp-${card.id}`}
								course={{
									...card,
									isSelected: selectedLearningPathCourseIds.includes(
										card.id
									)
								}}
								onClick={() => {
									const lp = learningPaths.find(
										(l) => l.id === card.id
									);
									if (lp) openDetails(lp);
								}}
								onSelect={(id) =>
									toggleCustomCourseSelection(
										'selectedLearningPathCourseIds',
										id
									)
								}
							/>
						)}
					/>
				)}
			</div>
		);
	}
);

const IndividualCourses = observer(
	({
		openDetails,
		search
	}: {
		openDetails: (activityId: number) => void;
		search: string;
	}) => {
		const { learningManagerStore } = useStores();
		const { activities, activityTypes, status } = learningManagerStore;

		const {
			draft: {
				customSetup: { selectedIndividualCourseIds }
			},
			toggleCustomCourseSelection
		} = useStepper();

		const query = search.trim().toLowerCase();
		const groups = activityTypes
			.map((type) => ({
				type,
				cards: activities
					.filter(
						(activity) =>
							activity.type_id === type.id &&
							(!query ||
								activity.name.toLowerCase().includes(query))
					)
					.map((activity, idx) => ({
						id: activity.id,
						title: activity.name,
						image:
							PLACEHOLDER_IMAGES[idx % PLACEHOLDER_IMAGES.length]
					}))
			}))
			.filter((group) => group.cards.length > 0);

		const isLoading =
			status.listActivities === Status.Pending ||
			status.listActivityTypes === Status.Pending;

		return (
			<div
				id="lms-ca-select-courses-individual"
				className={styles.learningWrapper}
			>
				<Heading
					icon={<Icon type="indiCourses" role="img" />}
					title="Individual Courses"
				/>
				{isLoading ? (
					<div className={styles.loading}>
						<Spin />
					</div>
				) : groups.length === 0 ? (
					<div className={styles.noResults}>No courses found</div>
				) : (
					<div className={styles.individualWrapper}>
						{groups.map(({ type, cards }) => (
							<Carousel
								key={type.id}
								label={type.name}
								data={cards}
								itemsPerPage={4}
								renderItem={(card) => (
									<CourseCard
										key={card.id}
										index={card.id}
										htmlId={`lms-ca-ind-course-${card.id}`}
										course={{
											...card,
											isSelected: selectedIndividualCourseIds.includes(
												card.id
											)
										}}
										onClick={() => openDetails(card.id)}
										onSelect={(id) =>
											toggleCustomCourseSelection(
												'selectedIndividualCourseIds',
												id
											)
										}
									/>
								)}
							/>
						))}
					</div>
				)}
			</div>
		);
	}
);

export default SelectCourses;
